import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Calendar, MapPin, Users, StickyNote, ArrowLeft, Car, Phone } from "lucide-react";
import { api } from "../lib/api";

export default function BookingDetail() {
  const { bookingId } = useParams();
  const [b, setB] = useState(null);
  useEffect(() => {
    api.get(`/bookings/${bookingId}`).then((r) => setB(r.data)).catch(() => setB(null));
  }, [bookingId]);

  if (!b) {
    return <div className="h-96 flex items-center justify-center">
      <div className="w-10 h-10 rounded-full border-2 border-gold border-t-transparent animate-spin" />
    </div>;
  }

  return (
    <section className="mx-auto max-w-4xl px-5 sm:px-8 py-12">
      <Link to="/bookings" className="text-sm text-white/60 hover:text-white inline-flex items-center gap-1 mb-6">
        <ArrowLeft size={14} /> Back to bookings
      </Link>

      <div className="flex items-center gap-3 mb-2 flex-wrap">
        {b.type === "ride"
          ? <span className="label-caps px-2.5 py-1 rounded-full bg-gold/10 border border-gold/30 text-gold">Chauffeur</span>
          : <span className="label-caps px-2.5 py-1 rounded-full bg-electric/10 border border-electric/30 text-electric">Rental</span>}
        <span className="label-caps text-white/50">Booking · {b.id}</span>
      </div>
      <h1 className="font-serif text-4xl sm:text-5xl mb-8">{b.vehicle_name}</h1>

      <div className="grid md:grid-cols-2 gap-6">
        <div className="glass rounded-3xl p-6 space-y-3">
          <div className="label-caps text-gold mb-2">Trip details</div>
          {b.type === "ride" ? (
            <>
              <Row icon={MapPin}>{b.pickup} → {b.dropoff}</Row>
              <Row icon={Calendar}>{new Date(b.pickup_datetime).toLocaleString()}</Row>
              <Row icon={Users}>{b.passenger_count} passenger(s) · {b.estimated_duration_hours} hrs</Row>
              {b.special_requests && <Row icon={StickyNote}>{b.special_requests}</Row>}
            </>
          ) : (
            <>
              <Row icon={MapPin}>Pickup: {b.pickup_location}</Row>
              <Row icon={MapPin}>Return: {b.return_location}</Row>
              <Row icon={Calendar}>{new Date(b.start_at).toLocaleString()} — {new Date(b.end_at).toLocaleString()}</Row>
              <Row icon={Car}>Billed: {b.days} day(s) · Deposit ${b.deposit?.toFixed(2)}</Row>
            </>
          )}
        </div>

        <div className="glass rounded-3xl p-6 space-y-3">
          <div className="label-caps text-gold mb-2">Payment</div>
          {b.type === "rental" && (
            <>
              <Row>Subtotal · <span className="text-white">${b.subtotal?.toFixed(2)}</span></Row>
              <Row>Tax · <span className="text-white">${b.tax?.toFixed(2)}</span></Row>
            </>
          )}
          <Row>Total · <span className="text-gold text-lg font-serif">${b.amount?.toFixed(2)}</span></Row>
          <Row>Status · <span className="text-white">{b.payment_status}</span></Row>
        </div>

        {b.type === "ride" && b.tracking && (
          <div className="glass rounded-3xl p-6 md:col-span-2 space-y-3">
            <div className="label-caps text-electric mb-2">Live tracking</div>
            <div className="text-sm text-white/70">
              Driver: <span className="text-white">{b.tracking.driver_name}</span> ·
              Vehicle plate: <span className="text-white">{b.tracking.vehicle_plate}</span>
            </div>
            <div className="text-sm text-white/70">Status: <span className="text-electric">{b.tracking.status}</span></div>
            {b.tracking.driver_phone && (
              <a href={`tel:${b.tracking.driver_phone}`} className="btn-outline-electric px-4 py-2 rounded-full text-xs inline-flex items-center gap-2 hover:text-white">
                <Phone size={14} /> Call driver
              </a>
            )}
            <p className="text-xs text-white/40 pt-2">
              Live map tracking will appear here once dispatch assigns a driver.
            </p>
          </div>
        )}

        {b.type === "rental" && (
          <div className="glass rounded-3xl p-6 md:col-span-2 space-y-3">
            <div className="label-caps text-gold mb-2">Pickup instructions</div>
            <p className="text-sm text-white/70 leading-relaxed">
              Bring your driver’s license and the payment card used at checkout.
              A refundable security deposit of <span className="text-gold">${b.deposit?.toFixed(2)}</span> will be
              authorised at pickup. Arrive 15 minutes before your start time for a walk-around inspection.
            </p>
          </div>
        )}
      </div>
    </section>
  );
}

const Row = ({ icon: Icon, children }) => (
  <div className="flex items-center gap-2 text-sm text-white/70">
    {Icon && <Icon size={14} className="text-gold" />}
    <span>{children}</span>
  </div>
);
